import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import Styled from 'styled';

import withDialog from 'lib/alerts/with-dialog';
import { RetryDialog } from 'lib/alerts/dialog';
import Localizable from 'lib/intl/localizable';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';

import reply from 'four-nets/icons/svg/icon_reply.svg';
import like from 'four-nets/icons/svg/icon_like.svg';
import likeFull from 'four-nets/icons/svg/icon_like-2.svg';
import others from 'four-nets/icons/svg/icon_others.svg';
import time from 'four-nets/icons/svg/icon_time.svg';
import { DeletedContent } from 'four-nets/common/deleted-content';
import { HideForUnpaidVendor } from 'four-nets/vendor/permissions';
import { UserGridListViewModel } from 'four-nets/user/user-grid-list-view';
import ContextButtonView from 'four-nets/ui/context-button/context-button-view';
import ActionSheet, { Action } from 'four-nets/ui-v2/action-sheet/action-sheet';

import defaultStyles from './comment-footer-view.scss';

export class CommentFooterViewModel {
    comment: Comment;

    constructor(options) {
        const { comment, userMentionable, userStore, categoryStore } = options;
        this.comment = comment;
        this.userMentionable = userMentionable;
        this.userStore = userStore;
    }

    reply = () => {
        this.userMentionable.mention({ username: this.comment.user.username });
    };
}

@Localizable()
@withDialog
@Styled(defaultStyles)
@observer
class CommentFooterView extends React.Component {
    @observable menuOpened = false;
    @observable editTimeout = 0;

    editCountDownInterval = null;

    static contextTypes = {
        authCheck: PropTypes.func,
        showLikers: PropTypes.func,
        writeMailToAuthor: PropTypes.func,
        complain: PropTypes.func,
        edit: PropTypes.func,
        delete: PropTypes.func,
    };

    componentDidMount() {
        this.editTimeout = this.props.commentFooterViewModel.comment.editTimeout;

        if (this.editTimeout > 0) {
            this.editCountDownInterval = setInterval(this.updateEditCountDown, 1000);
        }
    }

    componentWillUnmount() {
        clearInterval(this.editCountDownInterval);
    }

    updateEditCountDown = () => {
        this.editTimeout = this.props.commentFooterViewModel.comment.editTimeout;
    };

    open = () => {
        // Since user is loaded only on client
        // updating timeout before opening fixes #1465
        this.updateEditCountDown();
        this.menuOpened = true;
    };

    close = () => {
        this.menuOpened = false;
    };

    toggleLike = () => {
        const { commentFooterViewModel } = this.props;
        const { comment } = commentFooterViewModel;

        this.context.authCheck(comment.toggleLike.bind(comment), AuthOrigin.LikeForumMessage).catch(err => {
            this.props.showDialog(
                new RetryDialog({
                    message: err.message,
                    retry: this.toggleLike,
                })
            );
        });
    };

    showLikers = () => {
        const userGridListViewModel = new UserGridListViewModel({
            loadUsers: () => this.props.commentFooterViewModel.comment.loadLikes(),
            extractUsers: likeObject => likeObject.liker,
        });
        // received from ThreadView, so there is only one UserGridViewList per thread
        this.context.showLikers(userGridListViewModel);
    };

    // #FIXME maybe move authCheck to the context provider

    writeMailToAuthor = () => {
        this.close();
        this.context.authCheck(() => {
            this.context.writeMailToAuthor(this.props.commentFooterViewModel.comment);
            return Promise.resolve();
        }, AuthOrigin.NewMail);
    };

    complain = () => {
        this.close();
        this.context.authCheck(() => {
            this.context.complain(this.props.commentFooterViewModel.comment);
            return Promise.resolve();
        }, AuthOrigin.NewComplain);
    };

    edit = () => {
        this.close();
        this.context.authCheck(() => {
            this.context.edit(this.props.commentFooterViewModel.comment);
            return Promise.resolve();
        }, AuthOrigin.EditForumMessage);
    };

    delete = () => {
        this.close();
        this.context.authCheck(() => {
            this.context.delete(this.props.commentFooterViewModel.comment);
            return Promise.resolve();
        }, AuthOrigin.DeleteForumMessage);
    };

    render() {
        const { commentFooterViewModel, commentViewModel, styles, ...rest } = this.props;
        const { comment } = commentFooterViewModel;
        const isDeleted = comment.displayedContent instanceof DeletedContent;

        const isLocked = comment.thread && comment.thread.isLocked;

        const { isFirstMessage } = rest;

        const classNames = [styles.commentFooter];
        if (isFirstMessage) {
            classNames.push(styles.isFirstMessage);
        }

        if (commentViewModel.isDeleted) {
            classNames.push(styles.deleted);
        }

        return (
            <div className={classNames.join(' ')}>
                <div className={styles.date}>
                    <SVGIcon style={{ image: styles.moreIcon }} src={time} />
                    <FormattedDateAgoView value={comment.time} />
                </div>
                <HideForUnpaidVendor style={isFirstMessage ? { overlayButton: styles.hideForUnpaidVendor } : undefined}>
                    <span className={styles.likeButton}>
                        {comment.likesCount ? (
                            <span className={styles.likesCountWrapper}>
                                <ContextButtonView
                                    style={{ button: styles.button, content: styles.content }}
                                    icon={likeFull}
                                    label={comment.likesCount}
                                    onClick={this.showLikers}
                                />
                            </span>
                        ) : null}
                        {isLocked || isDeleted ? null : (
                            <span className={styles.likedByMeWrapper}>
                                <ContextButtonView
                                    style={{ button: styles.button, content: styles.content }}
                                    icon={comment.likesCount ? null : like}
                                    label={
                                        comment.isLikedByMe ? (
                                            <FormattedMessage id='UNLIKE_SHORT' />
                                        ) : (
                                            <FormattedMessage id='LIKE' />
                                        )
                                    }
                                    onClick={this.toggleLike}
                                />
                            </span>
                        )}
                    </span>
                </HideForUnpaidVendor>
                {isLocked || isDeleted ? null : (
                    <HideForUnpaidVendor>
                        <ContextButtonView
                            style={{ button: styles.button, content: styles.content }}
                            icon={reply}
                            label={<FormattedMessage id='REPLY' values={{ gender: 'v' }} />}
                            onClick={commentFooterViewModel.reply}
                        />
                    </HideForUnpaidVendor>
                )}
                {isDeleted ? null : (
                    <HideForUnpaidVendor>
                        <button onClick={this.open} className={styles.moreButton}>
                            <SVGIcon style={{ image: styles.moreIcon }} IconWithMargin src={others} />
                            <FormattedMessage id='BUTTON_LABEL_MORE'>
                                {msg => <span className={styles.more}>{msg}</span>}
                            </FormattedMessage>
                        </button>
                    </HideForUnpaidVendor>
                )}
                <ActionSheet open={this.menuOpened} onClose={this.close}>
                    {({ close }) => (
                        <>
                            <Action onClick={this.writeMailToAuthor}>
                                <FormattedMessage id='SEND_PRIVATE_MESSAGE' />
                            </Action>
                            <Action onClick={this.complain}>
                                <FormattedMessage id='REPORT_FORUM_MESSAGE' />
                            </Action>
                            {/* Positive editTimout means user still have time to edit the comment,
                exactly -1 means user is super staff and can edit indefinitely. */}
                            {this.editTimeout > 0 || this.editTimeout === -1 ? (
                                <Action onClick={this.edit}>
                                    <FormattedMessage id='EDIT_FORUM_MESSAGE' values={{ timeout: this.editTimeout }} />
                                </Action>
                            ) : null}
                            {/* Using editTimeout === -1 as a check if user is superStaff */}
                            {this.editTimeout === -1 ? (
                                <Action onClick={this.delete}>
                                    <FormattedMessage id='BUTTON_LABEL_DELETE' />
                                </Action>
                            ) : null}
                            {close}
                        </>
                    )}
                </ActionSheet>
            </div>
        );
    }
}

export default CommentFooterView;
